import React, { useState, useEffect } from 'react';
import {
  Search,
  Smartphone,
  CheckCircle2,
  Clock,
  Wrench,
  AlertCircle,
  Phone,
  User,
  ShieldCheck,
  Calendar,
  Layers,
  RefreshCw,
  FileText,
  BadgeCheck,
  HelpCircle,
  ExternalLink,
  MessageSquare,
  Lock,
  ChevronRight,
  Package,
  Info,
  DollarSign,
  Check,
  X,
  XCircle,
  PhoneCall,
  ArrowRight,
  Send
} from 'lucide-react';
import { CompanyInfo, ServiceTicket, TicketStatus } from '../types';
import {
  calculateTicketTotal,
  formatDateTR,
  formatTL,
  formatPhoneDisplay,
  sanitizePhoneInput,
  STATUS_COLORS,
  STATUS_LABELS,
  fetchServerData,
  getStoredTickets,
  saveTickets,
  getStoredCompanyInfo,
  formatTicketPaymentMethodDisplay,
  getTicketGrandTotal
} from '../utils/storage';

interface CustomerTrackingViewProps {
  companyInfo?: CompanyInfo;
  initialTicketId?: string;
  initialPhone?: string;
  onBackToApp?: () => void;
}

export const CustomerTrackingView: React.FC<CustomerTrackingViewProps> = ({
  companyInfo: initialCompanyInfo,
  initialTicketId = '',
  initialPhone = '',
  onBackToApp,
}) => {
  const [ticketIdInput, setTicketIdInput] = useState(initialTicketId);
  const [phoneInput, setPhoneInput] = useState(initialPhone);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(initialCompanyInfo || getStoredCompanyInfo());
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isAutoTrackedFromUrl, setIsAutoTrackedFromUrl] = useState(false);
  const [foundTicket, setFoundTicket] = useState<ServiceTicket | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);
  const [decisionModalType, setDecisionModalType] = useState<'approve' | 'reject' | null>(null);
  const [customerDecisionNote, setCustomerDecisionNote] = useState('');
  const [decisionFeedback, setDecisionFeedback] = useState<{ type: 'success' | 'info'; message: string } | null>(null);

  const handleCustomerDecision = async (decision: 'approve' | 'reject') => {
    if (!foundTicket) return;
    setIsSubmittingDecision(true);
    try {
      const newStatus: TicketStatus = decision === 'approve' ? 'Onaylandi' : 'Onaylanmadi';
      const timestampStr = new Date().toLocaleString('tr-TR');
      const noteText = customerDecisionNote.trim();
      const noteEntry = noteText
        ? `\n[${timestampStr}] ${noteText}`
        : `\n[${timestampStr}] ${decision === 'approve' ? 'Onaylandı' : 'Onaylanmadı'}`;

      // 1. Send to PHP backend if available
      try {
        await fetch('/api.php?action=customer_decision', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticketId: foundTicket.id,
            phone: foundTicket.customerPhone,
            decision,
            note: noteText,
          }),
        });
      } catch {}

      // 2. Update tickets in local storage
      const allTickets = getStoredTickets();
      const updatedTickets = allTickets.map(t => {
        if (t.id.trim().toUpperCase() === foundTicket.id.trim().toUpperCase()) {
          return {
            ...t,
            status: newStatus,
            technicianNotes: (t.technicianNotes || '') + noteEntry,
            lastUpdatedAt: new Date().toISOString(),
            lastUpdatedByName: 'Müşteri (Online Onay Paneli)',
          };
        }
        return t;
      });
      saveTickets(updatedTickets);

      const updatedFoundTicket: ServiceTicket = {
        ...foundTicket,
        status: newStatus,
        technicianNotes: (foundTicket.technicianNotes || '') + noteEntry,
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedByName: 'Müşteri (Online Onay Paneli)',
      };

      setFoundTicket(updatedFoundTicket);
      setDecisionModalType(null);
      setCustomerDecisionNote('');
      setDecisionFeedback({
        type: decision === 'approve' ? 'success' : 'info',
        message: decision === 'approve'
          ? 'Onarım onayınız başarıyla sisteme iletildi. Servis ekibimiz işlemlere başlamaktadır.'
          : 'Onarım ret talebiniz sisteme iletildi. Cihazınız iade hazırlığına alınacaktır.',
      });
    } catch (err: any) {
      console.error('Error saving customer decision:', err);
    } finally {
      setIsSubmittingDecision(false);
    }
  };

  // Check URL parameters on mount and when props change
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      let urlTicketId =
        urlParams.get('takip') ||
        urlParams.get('ticket') ||
        urlParams.get('no') ||
        urlParams.get('form') ||
        urlParams.get('formno') ||
        urlParams.get('form_no') ||
        urlParams.get('servis') ||
        urlParams.get('servisno') ||
        urlParams.get('servis_no') ||
        initialTicketId ||
        '';

      let urlPhone =
        urlParams.get('tel') ||
        urlParams.get('phone') ||
        urlParams.get('telefon') ||
        urlParams.get('gsm') ||
        urlParams.get('cep') ||
        urlParams.get('mobile') ||
        initialPhone ||
        '';

      // Fallback: check hash parameters if URL search was empty
      if ((!urlTicketId || !urlPhone) && window.location.hash.includes('?')) {
        const hashQuery = window.location.hash.substring(window.location.hash.indexOf('?'));
        const hashParams = new URLSearchParams(hashQuery);
        if (!urlTicketId) {
          urlTicketId =
            hashParams.get('takip') ||
            hashParams.get('ticket') ||
            hashParams.get('no') ||
            hashParams.get('form') ||
            hashParams.get('formno') ||
            hashParams.get('form_no') ||
            hashParams.get('servis') ||
            hashParams.get('servisno') ||
            hashParams.get('servis_no') ||
            '';
        }
        if (!urlPhone) {
          urlPhone =
            hashParams.get('tel') ||
            hashParams.get('phone') ||
            hashParams.get('telefon') ||
            hashParams.get('gsm') ||
            hashParams.get('cep') ||
            hashParams.get('mobile') ||
            '';
        }
      }

      if (urlTicketId) {
        setTicketIdInput(urlTicketId.trim().toUpperCase());
      }
      if (urlPhone) {
        setPhoneInput(urlPhone.trim());
      }

      if (urlTicketId && urlPhone) {
        setIsAutoTrackedFromUrl(true);
        performSearch(urlTicketId.trim(), urlPhone.trim());
      }
    } catch {}
  }, [initialTicketId, initialPhone]);

  const cleanPhone = (val: string) => {
    return val.replace(/\D/g, '').replace(/^0+/, '').replace(/^90/, '');
  };

  const performSearch = async (tId: string, phone: string) => {
    const cleanTId = tId.trim().toUpperCase();
    const targetDigits = cleanPhone(phone);

    if (!cleanTId) {
      setErrorMessage('Lütfen form / servis numaranızı giriniz.');
      return;
    }

    if (!targetDigits || targetDigits.length < 7) {
      setErrorMessage('Lütfen servis kaydında belirttiğiniz geçerli telefon numaranızı giriniz.');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setHasSearched(true);
    setFoundTicket(null);

    try {
      // 1. Try public query endpoint on PHP or Node backend first
      let serverTickets: ServiceTicket[] = [];
      try {
        const queryUrl = `/api.php?action=track&ticketId=${encodeURIComponent(cleanTId)}&phone=${encodeURIComponent(targetDigits)}&_t=${Date.now()}`;
        const res = await fetch(queryUrl, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.ticket) {
            setFoundTicket(data.ticket);
            if (data.company) {
              setCompanyInfo(data.company);
            }
            setIsLoading(false);
            return;
          }
        }
      } catch {}

      // 2. Fallback to fetchServerData or LocalStorage
      const serverData = await fetchServerData().catch(() => null);
      if (serverData?.company) {
        setCompanyInfo(serverData.company);
      }

      const allTickets: ServiceTicket[] = serverData?.tickets?.length
        ? serverData.tickets
        : getStoredTickets();

      const matched = allTickets.find(t => {
        const idMatches = t.id.trim().toUpperCase() === cleanTId;
        const storedPhoneDigits = cleanPhone(t.customerPhone || '');
        const phoneMatches =
          storedPhoneDigits.includes(targetDigits) ||
          targetDigits.includes(storedPhoneDigits) ||
          storedPhoneDigits.endsWith(targetDigits.slice(-7)) ||
          targetDigits.endsWith(storedPhoneDigits.slice(-7));

        return idMatches && phoneMatches;
      });

      if (matched) {
        setFoundTicket(matched);
      } else {
        // Check if ticket ID exists but phone didn't match
        const onlyIdMatched = allTickets.find(t => t.id.trim().toUpperCase() === cleanTId);
        if (onlyIdMatched) {
          setErrorMessage('Girdiğiniz servis numarası bulundu ancak kayıtlı telefon numarası uyuşmuyor.');
        } else {
          setErrorMessage('Belirtilen servis numarası ve telefon bilgisine ait aktif bir cihaz kaydı bulunamadı.');
        }
      }
    } catch (err: any) {
      setErrorMessage('Sorgulama sırasında bağlantı hatası oluştu: ' + (err?.message || 'Sunucuya ulaşılamadı.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(ticketIdInput, phoneInput);
  };

  // Status timeline steps
  const getTimelineSteps = (ticket: ServiceTicket) => {
    const isReturned = ticket.status === 'IadeEdildi' || ticket.status === 'IadeEdilecek';
    const isCancelled = ticket.status === 'Iptal';
    const isApprovalPending = ticket.status === 'MusteriOnayi';
    const isApproved = ticket.status === 'Onaylandi';
    const isApprovalRejected = ticket.status === 'Onaylanmadi';
    const isPartWaiting = ticket.status === 'ParcaBekliyor';
    const isSubService = ticket.status === 'AltServiste';

    const steps: Array<{
      key: string;
      label: string;
      desc: string;
      isPassed: boolean;
      isCurrent: boolean;
      isWarning?: boolean;
      isError?: boolean;
    }> = [
      {
        key: 'Kaydedildi',
        label: 'Kayıt Alındı & Teslim Alma',
        desc: 'Cihaz teslim alındı ve servis girişi yapıldı.',
        isPassed: true,
        isCurrent: ticket.status === 'Kaydedildi',
      },
    ];

    // Step 2: Arıza Tespiti & İnceleme
    const passedInvestigation = !['Kaydedildi'].includes(ticket.status);
    const isTesting = ticket.status === 'TestEdiliyor';
    const isLiquidDamage = ticket.status === 'Sivida';

    steps.push({
      key: 'Incelemede',
      label: 'Arıza Tespiti & İnceleme',
      desc:
        ticket.status === 'Incelemede'
          ? 'Teknisyen tarafından cihaz arızası ve gerekli parçalar inceleniyor.'
          : isTesting
          ? 'Cihazın onarım sonrası fonksiyon testleri yapılıyor.'
          : isLiquidDamage
          ? 'Cihazda sıvı teması tespit edildi; gerekli kontroller ve temizlik sürüyor.'
          : passedInvestigation
          ? 'Arıza tespiti ve teknik inceleme tamamlandı.'
          : 'Arıza tespiti ve teknik inceleme sırası bekleniyor.',
      isPassed: passedInvestigation,
      isCurrent: ticket.status === 'Incelemede' || isTesting || isLiquidDamage,
    });

    // Özel ara aşama: Yedek Parça Bekleniyorsa
    if (isPartWaiting) {
      steps.push({
        key: 'ParcaBekliyor',
        label: 'Yedek Parça Tedarik Süreci',
        desc: 'Onarım için gerekli parça tedarik ediliyor.',
        isPassed: true,
        isCurrent: true,
        isWarning: true,
      });
    }

    // Özel ara aşama: Dış Servisteyse
    if (isSubService) {
      steps.push({
        key: 'AltServiste',
        label: 'Dış Servis / Laboratuvar Süreci',
        desc: 'Cihaz uzman laboratuvar / dış servis biriminde işlem görüyor.',
        isPassed: true,
        isCurrent: true,
      });
    }

    // YALNIZCA cihaz aktif olarak onay sürecindeyse veya onay verilmiş/reddedilmişse gösterilir
    if (isApprovalPending) {
      steps.push({
        key: 'MusteriOnayi',
        label: 'Müşteri Fiyat / İşlem Onayı',
        desc: 'Arıza tespiti ve tutar belirlendi, müşteri onayı bekleniyor.',
        isPassed: false,
        isCurrent: true,
        isWarning: true,
      });
    } else if (isApproved) {
      steps.push({
        key: 'Onaylandi',
        label: 'Müşteri Onayı Alındı',
        desc: 'Müşteri onayı alındı, onarım sırasına geçirildi.',
        isPassed: true,
        isCurrent: true,
      });
    } else if (isApprovalRejected) {
      steps.push({
        key: 'Onaylanmadi',
        label: 'Onarım Onaylanmadı',
        desc: 'Müşteri tarafından onarım/fiyat onaylanmadı.',
        isPassed: true,
        isCurrent: true,
        isError: true,
      });
    }

    // Step 3: Onarım & Parça Değişimi
    const passedRepair = ['Tamirde', 'Hazir', 'TeslimEdildi'].includes(ticket.status);
    steps.push({
      key: 'Tamirde',
      label: 'Onarım & Parça Değişimi',
      desc:
        ticket.status === 'Tamirde'
          ? 'Cihazın gerekli parça değişimi ve teknik onarımı yapılıyor.'
          : passedRepair
          ? 'Onarım ve parça değişim işlemleri tamamlandı.'
          : 'Onarım ve parça değişim sırası bekleniyor.',
      isPassed: passedRepair,
      isCurrent: ticket.status === 'Tamirde',
    });

    // Step 4: Son Test & Kalite Kontrol
    const passedReady = ['Hazir', 'TeslimEdildi'].includes(ticket.status);
    steps.push({
      key: 'Hazir',
      label: 'Son Test & Kalite Kontrol',
      desc:
        ticket.status === 'Hazir'
          ? 'Tüm fonksiyon testleri tamamlandı. Cihaz teslim almaya hazır.'
          : passedReady
          ? 'Testler başarıyla yapıldı ve cihaz hazırlandı.'
          : 'Onarım sonrası fonksiyon testleri yapılacak.',
      isPassed: passedReady,
      isCurrent: ticket.status === 'Hazir',
    });

    // Step 5: Müşteriye Teslim Edildi
    steps.push({
      key: 'TeslimEdildi',
      label: 'Müşteriye Teslim Edildi',
      desc:
        ticket.status === 'TeslimEdildi'
          ? 'Cihaz kontrol edilerek müşteriye eksiksiz teslim edilmiştir.'
          : 'Cihaz teslimatı bekleniyor.',
      isPassed: ticket.status === 'TeslimEdildi',
      isCurrent: ticket.status === 'TeslimEdildi',
    });

    if (isReturned) {
      steps.push({
        key: 'Iade',
        label: ticket.status === 'IadeEdildi' ? 'Cihaz İade Edildi' : 'İade Hazırlanıyor',
        desc: 'Onarım yapılmadan veya müşteri talebiyle iade süreci.',
        isPassed: true,
        isCurrent: true,
        isError: true,
      });
    }

    if (isCancelled) {
      steps.push({
        key: 'Iptal',
        label: 'Kayıt İptal Edildi',
        desc: 'Servis talebi iptal edilmiştir.',
        isPassed: true,
        isCurrent: true,
        isError: true,
      });
    }

    return steps;
  };

  const getStatusBadge = (status: TicketStatus) => {
    switch (status) {
      case 'Hazir':
        return {
          bg: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
          dot: 'bg-emerald-400',
          icon: CheckCircle2,
          text: 'Onarım Tamamlandı (Teslimata Hazır)',
        };
      case 'TeslimEdildi':
        return {
          bg: 'bg-teal-500/10 border-teal-500/30 text-teal-300',
          dot: 'bg-teal-400',
          icon: BadgeCheck,
          text: 'Müşteriye Teslim Edildi',
        };
      case 'MusteriOnayi':
        return {
          bg: 'bg-orange-500/10 border-orange-500/30 text-orange-300',
          dot: 'bg-orange-400 animate-pulse',
          icon: Clock,
          text: 'Müşteri Fiyat Onayı Bekliyor',
        };
      case 'Onaylandi':
        return {
          bg: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
          dot: 'bg-emerald-400',
          icon: CheckCircle2,
          text: 'Müşteri Onayı Alındı',
        };
      case 'Onaylanmadi':
        return {
          bg: 'bg-rose-500/10 border-rose-500/30 text-rose-300',
          dot: 'bg-rose-400',
          icon: AlertCircle,
          text: 'Müşteri Onay Vermedi (İptal/İade)',
        };
      case 'Tamirde':
        return {
          bg: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
          dot: 'bg-blue-400 animate-pulse',
          icon: Wrench,
          text: 'Cihaz Onarımda / Tamir Ediliyor',
        };
      case 'Incelemede':
        return {
          bg: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-300',
          dot: 'bg-indigo-400 animate-pulse',
          icon: Search,
          text: 'Arıza Tespiti & İncelemede',
        };
      case 'TestEdiliyor':
        return {
          bg: 'bg-sky-500/10 border-sky-500/30 text-sky-300',
          dot: 'bg-sky-400 animate-pulse',
          icon: CheckCircle2,
          text: 'Cihaz Test Ediliyor',
        };
      case 'Sivida':
        return {
          bg: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
          dot: 'bg-blue-400 animate-pulse',
          icon: AlertTriangle,
          text: 'Sıvı Teması İşlemi Yapılıyor',
        };
      case 'ParcaBekliyor':
        return {
          bg: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
          dot: 'bg-amber-400 animate-pulse',
          icon: Package,
          text: 'Yedek Parça Tedarik Ediliyor',
        };
      case 'AltServiste':
        return {
          bg: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300',
          dot: 'bg-cyan-400 animate-pulse',
          icon: Wrench,
          text: 'Dış Servis / Laboratuvarda',
        };
      case 'IadeEdildi':
      case 'IadeEdilecek':
      case 'Iptal':
        return {
          bg: 'bg-rose-500/10 border-rose-500/30 text-rose-300',
          dot: 'bg-rose-400',
          icon: AlertCircle,
          text: STATUS_LABELS[status] || status,
        };
      default:
        return {
          bg: 'bg-slate-800 border-slate-700 text-slate-300',
          dot: 'bg-slate-400',
          icon: Clock,
          text: STATUS_LABELS[status] || status,
        };
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-start items-center p-4 py-6 sm:py-10 relative overflow-y-auto font-sans">
      {/* Background Decorative Glows */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-600/10 blur-[130px] rounded-full pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-indigo-600/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="w-full max-w-2xl relative z-10 space-y-6">
        {/* Top Header / Company Info */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/90 border border-slate-800 rounded-3xl p-5 shadow-2xl backdrop-blur-xl">
          <div className="flex items-center space-x-3.5 text-left w-full sm:w-auto">
            {companyInfo.logoUrl ? (
              <div className="w-14 h-14 rounded-2xl bg-white p-1.5 shadow-lg shadow-blue-500/20 shrink-0 border border-slate-700 flex items-center justify-center">
                <img
                  src={companyInfo.logoUrl}
                  alt={companyInfo.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/20 shrink-0">
                <Smartphone className="w-7 h-7" />
              </div>
            )}
            <div>
              <h1 className="text-base sm:text-lg font-black text-white leading-tight">
                {companyInfo.name}
              </h1>
            </div>
          </div>
        </div>

        {/* Query Input Card */}
        <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-6 sm:p-7 shadow-2xl backdrop-blur-xl space-y-5">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-400" />
              Cihaz Durum Sorgulama
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Servis fişinizdeki <strong className="text-slate-200">Form Numarası</strong> ve <strong className="text-slate-200">Telefon Numaranızı</strong> girerek durumu anında öğrenin.
            </p>
          </div>

          {errorMessage && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-4 text-rose-300 text-xs flex items-start space-x-2.5 animate-fadeIn">
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <div className="font-semibold leading-relaxed">{errorMessage}</div>
            </div>
          )}

          <form onSubmit={handleSearchSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Ticket ID Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-300 block">
                  Servis / Form Numarası *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                    <FileText className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    required
                    placeholder="TS-1001"
                    value={ticketIdInput}
                    onChange={e => setTicketIdInput(e.target.value.toUpperCase())}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-mono font-bold tracking-wider uppercase transition-all"
                  />
                </div>
                <span className="text-[10px] text-slate-500">Servis kabul fişinizdeki numara</span>
              </div>

              {/* Phone Input */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-300">
                    Telefon Numaranız *
                  </label>
                  {phoneInput && (
                    <span className="text-[10px] font-mono font-medium text-blue-400 bg-blue-950/60 border border-blue-800/60 px-1.5 py-0.5 rounded">
                      {formatPhoneDisplay(phoneInput)}
                    </span>
                  )}
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                    <Phone className="w-4 h-4" />
                  </div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={11}
                    required
                    placeholder="Telefon Numaranız"
                    value={phoneInput}
                    onChange={e => setPhoneInput(sanitizePhoneInput(e.target.value))}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 font-mono text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 font-medium transition-all"
                  />
                </div>
                <span className="text-[10px] text-slate-500">Kayıt esnasında verdiğiniz GSM (Birleşik giriniz)</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-2 flex flex-col sm:flex-row gap-2.5">
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-600/30 flex items-center justify-center space-x-2 transition-all hover:scale-[1.01] active:scale-[0.99] cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Sorgulanıyor...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>Cihaz Durumunu Sorgula</span>
                  </>
                )}
              </button>

              {foundTicket && (
                <button
                  type="button"
                  onClick={() => {
                    performSearch(ticketIdInput, phoneInput);
                  }}
                  className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold border border-slate-700 flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                  title="En son durumu yenile"
                >
                  <RefreshCw className="w-4 h-4 text-blue-400" />
                  <span>Yenile</span>
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Found Ticket Result Card */}
        {foundTicket && (
          <div className="bg-slate-900/95 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl backdrop-blur-xl space-y-6 animate-fadeIn">
            {/* Decision Feedback Alert */}
            {decisionFeedback && (
              <div className={`p-4 rounded-2xl border text-xs flex items-start justify-between gap-3 animate-fadeIn ${
                decisionFeedback.type === 'success'
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
                  : 'bg-blue-500/15 border-blue-500/40 text-blue-200'
              }`}>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                  <span className="font-semibold">{decisionFeedback.message}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setDecisionFeedback(null)}
                  className="text-slate-400 hover:text-white p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Top Status Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-800 pb-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400">Servis Fişi:</span>
                  <span className="text-base sm:text-lg font-mono font-black text-blue-400 bg-blue-500/10 px-2.5 py-0.5 rounded-lg border border-blue-500/20">
                    {foundTicket.id}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white">
                  {foundTicket.deviceBrand} {foundTicket.deviceModel}
                </h3>
                <div className="text-xs text-slate-400 flex items-center gap-2">
                  <span>Kategori: <strong className="text-slate-300">{foundTicket.deviceType}</strong></span>
                  {foundTicket.serialNumber && (
                    <span>• Seri/IMEI: <strong className="text-slate-300 font-mono">{foundTicket.serialNumber}</strong></span>
                  )}
                </div>
              </div>

              {/* Status Badge */}
              {(() => {
                const badge = getStatusBadge(foundTicket.status);
                const Icon = badge.icon;
                return (
                  <div className={`px-3.5 py-2 rounded-2xl border flex items-center gap-2 text-xs font-bold ${badge.bg}`}>
                    <span className={`w-2.5 h-2.5 rounded-full ${badge.dot}`} />
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{badge.text}</span>
                  </div>
                );
              })()}
            </div>

            {/* Customer Approval & Decision Screen (Onay & Karar Paneli) */}
            {foundTicket.status === 'MusteriOnayi' && (
              <div className="bg-gradient-to-br from-orange-950/40 via-slate-900/90 to-amber-950/30 border-2 border-orange-500/50 rounded-3xl p-5 sm:p-6 text-orange-200 shadow-xl shadow-orange-500/10 space-y-4 animate-fadeIn">
                <div className="flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-2xl bg-orange-500/20 border border-orange-500/40 flex items-center justify-center shrink-0 text-orange-300">
                    <Clock className="w-5 h-5 animate-pulse" />
                  </div>
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="font-bold text-orange-100 text-sm sm:text-base flex items-center gap-2">
                        <span>Fiyat ve Onarım Onayınız Bekleniyor</span>
                      </h4>
                      <span className="text-[10px] bg-orange-500/30 text-orange-200 px-2.5 py-0.5 rounded-full font-bold border border-orange-500/40 animate-pulse">
                        Onay Gerekli
                      </span>
                    </div>
                    <p className="text-xs text-orange-200/90 leading-relaxed">
                      Cihazınızın arıza tespiti tamamlanmış ve yapılacak işlemler belirlenmiştir. Servisimizin onarıma başlayabilmesi için aşağıdaki butonlardan onay verebilir, reddedebilir veya işletmemizi doğrudan arayabilirsiniz.
                    </p>
                  </div>
                </div>

                {/* Price & Summary Highlight in Approval Box */}
                <div className="p-3.5 bg-slate-950/80 rounded-2xl border border-orange-500/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <span className="text-[11px] text-slate-400 font-semibold block">Tahmini Onarım / Parça Tutarı (KDV Dahil):</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl sm:text-2xl font-black text-emerald-400 font-mono">
                        {formatTL(getTicketGrandTotal(foundTicket))}
                      </span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => setDecisionModalType('approve')}
                      className="flex-1 sm:flex-none px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-emerald-500/20 active:scale-95"
                    >
                      <CheckCircle2 className="w-4 h-4 text-emerald-200" />
                      <span>Onarımı Onayla</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDecisionModalType('reject')}
                      className="flex-1 sm:flex-none px-4 py-2.5 bg-rose-600/80 hover:bg-rose-600 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-rose-500/20 active:scale-95"
                    >
                      <XCircle className="w-4 h-4 text-rose-200" />
                      <span>Onaylama / Reddet</span>
                    </button>
                  </div>
                </div>

                {/* Direct Call & WhatsApp to Business for Approval/Rejection */}
                <div className="p-3.5 bg-slate-900/90 rounded-2xl border border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                  <div className="space-y-0.5">
                    <span className="font-bold text-slate-200 flex items-center gap-1.5">
                      <PhoneCall className="w-3.5 h-3.5 text-blue-400" />
                      <span>Onay / Red İçin İşletme İletişim Hattı</span>
                    </span>
                    <p className="text-[11px] text-slate-400 leading-snug">
                      Onay veya bilgi vermek için işletmemizi doğrudan arayabilir veya WhatsApp üzerinden hemen bildirebilirsiniz.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {companyInfo.phone && (
                      <a
                        href={`tel:${cleanPhone(companyInfo.phone)}`}
                        className="px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 transition-colors shadow-xs"
                        title={`İşletmeyi Ara: ${companyInfo.phone}`}
                      >
                        <Phone className="w-3.5 h-3.5" />
                        <span>Ara</span>
                        <span className="text-[11px] font-mono opacity-90">({companyInfo.phone})</span>
                      </a>
                    )}
                    {companyInfo.phone && (
                      <a
                        href={`https://wa.me/${cleanPhone(companyInfo.phone)}?text=${encodeURIComponent(`Merhaba, ${foundTicket.id} nolu cihazım (${foundTicket.deviceBrand} ${foundTicket.deviceModel}) için onarım onay/red durumumu bildirmek istiyorum.`)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold flex items-center justify-center transition-colors shadow-xs shrink-0"
                        title="WhatsApp ile Bildir"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Approved Status Banner */}
            {foundTicket.status === 'Onaylandi' && (
              <div className="bg-emerald-500/15 border border-emerald-500/40 rounded-2xl p-4 text-emerald-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-lg shadow-emerald-500/5 animate-fadeIn">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0 text-emerald-300">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-emerald-100 text-xs sm:text-sm">Onarım Onayınız Alındı</h4>
                    <p className="text-[11px] text-emerald-300/90">Cihazınızın onarım onay kaydı oluşturuldu, teknisyenimiz işleme başlamaktadır.</p>
                  </div>
                </div>
                {companyInfo.phone && (
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={`tel:${cleanPhone(companyInfo.phone)}`}
                      className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 transition-colors shrink-0"
                      title={`İşletmeyi Ara: ${companyInfo.phone}`}
                    >
                      <Phone className="w-3.5 h-3.5" />
                      <span>Ara ({companyInfo.phone})</span>
                    </a>
                    <a
                      href={`https://wa.me/${cleanPhone(companyInfo.phone)}?text=${encodeURIComponent(`Merhaba, ${foundTicket.id} nolu onaylanan cihazım (${foundTicket.deviceBrand} ${foundTicket.deviceModel}) hakkında bilgi almak istiyorum.`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl font-bold flex items-center justify-center transition-colors shrink-0"
                      title="WhatsApp ile İletişim"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Rejected Status Banner */}
            {foundTicket.status === 'Onaylanmadi' && (
              <div className="bg-rose-500/15 border border-rose-500/40 rounded-2xl p-4 text-rose-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-lg shadow-rose-500/5 animate-fadeIn">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center shrink-0 text-rose-300">
                    <XCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-rose-100 text-xs sm:text-sm">Onarım Onaylanmadı (İptal Edildi)</h4>
                    <p className="text-[11px] text-rose-300/90">Cihazınız üzerinde işlem yapılmadan iade hazırlığına alınmıştır.</p>
                  </div>
                </div>
                {companyInfo.phone && (
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={`tel:${cleanPhone(companyInfo.phone)}`}
                      className="px-3.5 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 transition-colors shrink-0"
                      title={`İşletmeyi Ara: ${companyInfo.phone}`}
                    >
                      <Phone className="w-3.5 h-3.5" />
                      <span>Ara ({companyInfo.phone})</span>
                    </a>
                    <a
                      href={`https://wa.me/${cleanPhone(companyInfo.phone)}?text=${encodeURIComponent(`Merhaba, ${foundTicket.id} nolu cihazım hakkında bilgi almak istiyorum.`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 bg-rose-700 hover:bg-rose-600 text-white rounded-xl font-bold flex items-center justify-center transition-colors shrink-0"
                      title="WhatsApp ile İletişim"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Quick Summary Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-slate-950/80 p-3.5 rounded-2xl border border-slate-800/80 space-y-1">
                <span className="text-[11px] font-semibold text-slate-400 block flex items-center gap-1">
                  <User className="w-3.5 h-3.5 text-blue-400" />
                  Müşteri Bilgisi
                </span>
                <p className="text-xs font-bold text-slate-200 truncate">{foundTicket.customerName}</p>
                <p className="text-[11px] text-slate-400 font-mono">{formatPhoneDisplay(foundTicket.customerPhone)}</p>
              </div>

              <div className="bg-slate-950/80 p-3.5 rounded-2xl border border-slate-800/80 space-y-1">
                <span className="text-[11px] font-semibold text-slate-400 block flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-blue-400" />
                  Kayıt Tarihi
                </span>
                <p className="text-xs font-bold text-slate-200">{formatDateTR(foundTicket.createdAt)}</p>
                <p className="text-[11px] text-slate-400">
                  {foundTicket.deliveryDate ? `Teslim: ${formatDateTR(foundTicket.deliveryDate)}` : 'Servis sürecinde'}
                </p>
              </div>

              <div className="bg-slate-950/80 p-3.5 rounded-2xl border border-slate-800/80 space-y-1">
                <span className="text-[11px] font-semibold text-slate-400 block flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                  Toplam Onarım Tutarı
                </span>
                <p className="text-sm font-black text-emerald-400 font-mono flex items-center gap-1">
                  <span>{formatTL(getTicketGrandTotal(foundTicket))}</span>
                </p>
                <p className="text-[10px] text-slate-400">
                  Ödeme Durumu: <strong className="text-slate-300">
                    {foundTicket.paymentStatus === 'Odendi'
                      ? 'Ödendi'
                      : foundTicket.paymentStatus === 'KismiOdendi'
                      ? 'Kısmi Ödendi'
                      : foundTicket.paymentStatus === 'IadeEdildi'
                      ? 'İade Edildi'
                      : foundTicket.status === 'MusteriOnayi'
                      ? 'Onay Bekliyor'
                      : 'Ödeme Bekliyor'}
                  </strong>
                </p>
              </div>
            </div>

            {/* Issue Description Box */}
            <div className="bg-slate-950/80 p-4 rounded-2xl border border-slate-800/80 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-300">
                <Wrench className="w-4 h-4 text-amber-400" />
                <span>Bildirilen Arıza / Müşteri Şikayeti</span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed bg-slate-900/60 p-3 rounded-xl border border-slate-800 font-medium">
                {foundTicket.issueDescription || 'Belirtilmedi'}
              </p>
            </div>

            {/* Stage Timeline */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                  <Layers className="w-4 h-4 text-blue-400" />
                  <span>Servis İlerleme Aşamaları</span>
                </h4>
                <span className="text-[11px] text-slate-500">Canlı Durum Akışı</span>
              </div>

              <div className="space-y-2.5">
                {getTimelineSteps(foundTicket).map((step, idx) => {
                  const isCurrent = step.isCurrent;
                  const isPassed = step.isPassed;
                  const isWarning = step.isWarning;
                  const isError = step.isError;

                  return (
                    <div
                      key={step.key}
                      className={`p-3.5 rounded-2xl border transition-all flex items-start space-x-3.5 ${
                        isCurrent && isWarning
                          ? 'bg-orange-500/10 border-orange-500/40 shadow-md shadow-orange-500/5'
                          : isCurrent && isError
                          ? 'bg-rose-500/10 border-rose-500/40 shadow-md shadow-rose-500/5'
                          : isCurrent
                          ? 'bg-blue-600/10 border-blue-500/40 shadow-md shadow-blue-500/5'
                          : isPassed
                          ? 'bg-slate-950/60 border-slate-800 text-slate-300'
                          : 'bg-slate-950/20 border-slate-900 text-slate-600 opacity-60'
                      }`}
                    >
                      <div
                        className={`w-7 h-7 rounded-xl flex items-center justify-center shrink-0 font-bold text-xs ${
                          isCurrent && isWarning
                            ? 'bg-orange-500 text-white shadow-xs'
                            : isCurrent && isError
                            ? 'bg-rose-500 text-white shadow-xs'
                            : isCurrent
                            ? 'bg-blue-600 text-white shadow-xs'
                            : isPassed && !isCurrent
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-slate-800 text-slate-500'
                        }`}
                      >
                        {isPassed && !isCurrent ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : isCurrent && isWarning ? (
                          <Clock className="w-4 h-4 text-white animate-spin" />
                        ) : (
                          idx + 1
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`text-xs font-bold ${
                              isCurrent && isWarning
                                ? 'text-orange-300'
                                : isCurrent && isError
                                ? 'text-rose-300'
                                : isCurrent
                                ? 'text-blue-300'
                                : isPassed
                                ? 'text-slate-200'
                                : 'text-slate-500'
                            }`}
                          >
                            {step.label}
                          </span>
                          {isCurrent && (
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse border ${
                                isWarning
                                  ? 'bg-orange-500/20 text-orange-300 border-orange-500/40'
                                  : isError
                                  ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                                  : 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                              }`}
                            >
                              {isWarning ? 'Onay Bekliyor' : isError ? 'İptal / Red' : 'Şu Anki Aşama'}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Performed Actions / Repair Details (Yapılacak İşlem / Yapılan İşlemler) */}
            {foundTicket.performedActions && foundTicket.performedActions.trim() && (
              <div className={`p-4 rounded-2xl border space-y-2.5 shadow-lg ${
                foundTicket.status === 'MusteriOnayi'
                  ? 'bg-slate-950/80 border-orange-500/40 shadow-orange-500/5'
                  : 'bg-slate-950/80 border-emerald-500/30 shadow-emerald-500/5'
              }`}>
                <div className="flex items-center justify-between">
                  <h4 className={`text-xs font-bold flex items-center gap-1.5 ${
                    foundTicket.status === 'MusteriOnayi' ? 'text-orange-300' : 'text-emerald-400'
                  }`}>
                    {foundTicket.status === 'MusteriOnayi' ? (
                      <>
                        <Clock className="w-4 h-4 text-orange-400" />
                        <span>Cihaza Yapılacak İşlem &amp; Teklif Detayı</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Cihaza Yapılan İşlem &amp; Onarım Raporu</span>
                      </>
                    )}
                  </h4>
                  <span className="text-[10px] px-2.5 py-0.5 rounded-full font-bold border flex items-center gap-1 bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                    <ShieldCheck className="w-3 h-3" />
                    Teknik Servis
                  </span>
                </div>
                <div className="p-3 bg-slate-900/90 rounded-xl border border-slate-800 text-xs text-slate-200 leading-relaxed font-medium whitespace-pre-line">
                  {foundTicket.performedActions}
                </div>
              </div>
            )}

            {/* Replaced Parts & Labor (Kullanılan Parçalar ve İşçilik) */}
            {((foundTicket.parts && foundTicket.parts.length > 0) || (foundTicket.laborCost !== undefined && foundTicket.laborCost > 0)) && (() => {
              const isVatExcluded = (foundTicket.vatRate || 0) > 0;
              const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(
                foundTicket.parts || [],
                foundTicket.laborCost || 0,
                foundTicket.vatRate || 0,
                foundTicket.discount || 0,
                foundTicket.isVatIncluded || false
              );

              return (
                <div className="bg-slate-950/80 p-4 rounded-2xl border border-slate-800/80 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                      <Package className="w-4 h-4 text-purple-400" />
                      <span>Kullanılan Parçalar &amp; İşçilik</span>
                    </h4>
                    <div className="flex items-center gap-1.5">
                      {foundTicket.isUnderWarranty && (
                        <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-bold border border-emerald-500/30">
                          Garanti Kapsamında
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="divide-y divide-slate-800/80 text-xs">
                    {foundTicket.parts && foundTicket.parts.map((p, idx) => (
                      <div key={idx} className="py-2.5 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                          <span className="font-semibold text-slate-300">{p.name}</span>
                          <span className="text-[10px] text-slate-500 bg-slate-900 px-1.5 py-0.5 rounded">x{p.quantity || 1}</span>
                        </div>
                        <span className="font-mono font-bold text-slate-300">
                          {formatTL((p.unitPrice || 0) * (p.quantity || 1))}
                        </span>
                      </div>
                    ))}
                    {foundTicket.laborCost !== undefined && foundTicket.laborCost > 0 && (
                      <div className="py-2.5 flex items-center justify-between text-slate-300">
                        <div className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                          <span>Teknik Servis &amp; Onarım İşçilik Bedeli</span>
                        </div>
                        <span className="font-mono font-bold text-slate-300">
                          {formatTL(foundTicket.laborCost)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Yeni karma KDV modeli: parça +KDV, işçilik KDV dahil; net/KDV kırılımını göster */}
                  {isVatExcluded && (
                    <div className="pt-2.5 mt-2 border-t border-slate-800/90 space-y-1.5 text-xs">
                      <div className="flex justify-between text-slate-400">
                        <span>Net Ara Toplam:</span>
                        <span className="font-mono text-slate-300">{formatTL(subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-orange-400">
                        <span>KDV (%20):</span>
                        <span className="font-mono font-semibold">+{formatTL(vatAmount)}</span>
                      </div>
                      <div className="flex justify-between items-center text-emerald-400 font-bold pt-1.5 border-t border-slate-800/80 text-sm">
                        <span>Genel Toplam (KDV Dahil):</span>
                        <span className="font-mono font-black">{formatTL(grandTotal)}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Company Contact & Support Information */}
            <div className="bg-gradient-to-r from-slate-900 to-indigo-950/60 border border-slate-800 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
              <div className="space-y-1">
                <span className="font-bold text-slate-200 block">{companyInfo.name}</span>
                <p className="text-slate-400">{companyInfo.address}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {companyInfo.phone && (
                  <a
                    href={`tel:${cleanPhone(companyInfo.phone)}`}
                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold flex items-center gap-1.5 transition-colors shadow-xs"
                    title={`İşletmeyi Ara: ${companyInfo.phone}`}
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>Ara ({companyInfo.phone})</span>
                  </a>
                )}
                {companyInfo.phone && (
                  <a
                    href={`https://wa.me/${cleanPhone(companyInfo.phone)}?text=${encodeURIComponent(`Merhaba, ${foundTicket.id} nolu cihazım hakkında bilgi almak istiyorum.`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl font-bold flex items-center justify-center transition-colors shadow-xs shrink-0"
                    title="WhatsApp ile İletişime Geç"
                  >
                    <MessageSquare className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Customer Decision Confirmation Modal */}
        {decisionModalType && foundTicket && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl space-y-5 text-slate-200">
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <div className="flex items-center gap-2.5">
                  {decisionModalType === 'approve' ? (
                    <div className="p-2 rounded-xl bg-emerald-500/20 text-emerald-400">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                  ) : (
                    <div className="p-2 rounded-xl bg-rose-500/20 text-rose-400">
                      <XCircle className="w-5 h-5" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-base font-bold text-white">
                      {decisionModalType === 'approve' ? 'Onarımı Onayla' : 'Onarımı Reddet / İptal Et'}
                    </h3>
                    <span className="text-[11px] text-slate-400 font-mono">Form #{foundTicket.id}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setDecisionModalType(null)}
                  className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3 text-xs">
                <p className="text-slate-300 leading-relaxed">
                  {decisionModalType === 'approve'
                    ? `Form #${foundTicket.id} nolu cihazınız (${foundTicket.deviceBrand} ${foundTicket.deviceModel}) için belirlenen ${formatTL(getTicketGrandTotal(foundTicket))} tutarındaki onarım işlemini onaylamak üzeresiniz.`
                    : `Form #${foundTicket.id} nolu cihazınız için onarım işlemini onaylamadığınızda işlem iptal edilecek ve cihazınız teslim için hazırlanacaktır.`}
                </p>

                <div className="space-y-1.5">
                  <label className="block text-slate-400 font-semibold text-[11px]">
                    {decisionModalType === 'approve'
                      ? 'Servise İletmek İstediğiniz Not (İsteğe Bağlı):'
                      : 'Ret Sebebi veya Açıklamanız (İsteğe Bağlı):'}
                  </label>
                  <textarea
                    rows={2}
                    value={customerDecisionNote}
                    onChange={(e) => setCustomerDecisionNote(e.target.value)}
                    placeholder={
                      decisionModalType === 'approve'
                        ? 'Cihazı teslim alırken kablosunu da unutmayın lütfen...'
                        : 'Fiyat yüksek geldiği için şu an yaptırmak istemiyorum...'
                    }
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                  />
                </div>
              </div>

              {/* Direct call business link inside modal */}
              {companyInfo.phone && (
                <div className="bg-slate-950/60 p-2.5 rounded-xl border border-slate-800/80 flex items-center justify-between text-[11px]">
                  <span className="text-slate-400">Görüşmek için:</span>
                  <a
                    href={`tel:${cleanPhone(companyInfo.phone)}`}
                    className="text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1"
                  >
                    <Phone className="w-3 h-3" />
                    <span>{companyInfo.phone}</span>
                  </a>
                </div>
              )}

              <div className="flex items-center justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  disabled={isSubmittingDecision}
                  onClick={() => setDecisionModalType(null)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-semibold text-xs transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  disabled={isSubmittingDecision}
                  onClick={() => handleCustomerDecision(decisionModalType)}
                  className={`px-4 py-2 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all ${
                    decisionModalType === 'approve'
                      ? 'bg-emerald-600 hover:bg-emerald-500 shadow-md hover:shadow-emerald-500/20'
                      : 'bg-rose-600 hover:bg-rose-500 shadow-md hover:shadow-rose-500/20'
                  }`}
                >
                  {isSubmittingDecision ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>İşleniyor...</span>
                    </>
                  ) : decisionModalType === 'approve' ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Evet, Onaylıyorum</span>
                    </>
                  ) : (
                    <>
                      <X className="w-3.5 h-3.5" />
                      <span>Evet, Reddet</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer info */}
        <div className="text-center text-xs text-slate-500 pt-2 pb-6">
          © {new Date().getFullYear()} {companyInfo.name} • Canlı Cihaz Durum Takip Sistemi
        </div>
      </div>
    </div>
  );
};
